import React, { useRef, useState, useContext, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { NavbarContext } from "../NavbarContext";
import { useAuth } from "../AuthContext";
import { useTranslation } from "react-i18next";

import "./styles/Navbar.css";

import logoPng from "../medias/png/logo/vf_logo.png";
import frFlag from "../medias/png/logo/france.png";
import gbFlag from "../medias/png/logo/royaume-uni.png";

const MOBILE_BREAKPOINT = 599;

const Navbar = () => {
  const menuMobileRef = useRef(null);
  const menuMobileIconRef = useRef(null);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= MOBILE_BREAKPOINT);
  const { navbarActive, setNavbarActive, setScrollPosition } = useContext(NavbarContext);
  const { user, accountType } = useAuth();

  const { t, i18n } = useTranslation();

  const currentLang =
    (i18n.resolvedLanguage || i18n.language || "fr").startsWith("en") ? "en" : "fr";

  const changeLanguage = (lng) => {
    if (!lng || lng === currentLang) return;
    i18n.changeLanguage(lng);
  };

  const handleNavbarToggle = () => {
    if (!navbarActive) {
      const currentScroll = window.scrollY || document.documentElement.scrollTop;
      setScrollPosition(currentScroll);
    }
    setNavbarActive(!navbarActive);
  };

  const resetNavbar = () => {
    setNavbarActive(false);
    setScrollPosition(0);
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const FlagSwitcher = () => (
    <div className="navbar-lang" aria-label="language switcher">
      <button
        type="button"
        className={`navbar-lang-btn ${currentLang === "fr" ? "active" : ""}`}
        onClick={() => changeLanguage("fr")}
        aria-label="Français"
        title="Français"
      >
        <img className="navbar-flag" src={frFlag} alt="FR" />
      </button>

      <button
        type="button"
        className={`navbar-lang-btn ${currentLang === "en" ? "active" : ""}`}
        onClick={() => changeLanguage("en")}
        aria-label="English"
        title="English"
      >
        <img className="navbar-flag" src={gbFlag} alt="EN" />
      </button>
    </div>
  );

  return (
    <header className="navbar">
      {!isMobile ? (
        <div className="navbar-desktop">
          <Link to="/" className="navbar-logo" onClick={resetNavbar}>
            <img
              src={logoPng}
              alt="Logo VF Cycling"
              className="navbar-logo-image"
              loading="eager"
              width="500"
              height="500"
            />
          </Link>

          <nav aria-label="desktop navigation" className="navbar-center-links">
            <NavLink
              to="/"
              className={({ isActive }) => (isActive ? "navbar-link selected" : "navbar-link")}
              onClick={resetNavbar}
            >
              {t("navbar.home")}
            </NavLink>

            <NavLink
              to="/profil"
              className={({ isActive }) => (isActive ? "navbar-link selected" : "navbar-link")}
              onClick={resetNavbar}
            >
              {t("navbar.team")}
            </NavLink>

            <NavLink
              to="/calendar"
              className={({ isActive }) => (isActive ? "navbar-link selected" : "navbar-link")}
              onClick={resetNavbar}
            >
              {t("navbar.calendar")}
            </NavLink>

            <NavLink
              to="/quoting"
              className={({ isActive }) => (isActive ? "navbar-link selected" : "navbar-link")}
              onClick={resetNavbar}
            >
              {t("navbar.quoting")}
            </NavLink>

            <NavLink
              to="/scale"
              className={({ isActive }) => (isActive ? "navbar-link selected" : "navbar-link")}
              onClick={resetNavbar}
            >
              {t("navbar.scale")}
            </NavLink>

            <NavLink
              to="/rules"
              className={({ isActive }) => (isActive ? "navbar-link selected" : "navbar-link")}
              onClick={resetNavbar}
            >
              {t("navbar.rules")}
            </NavLink>

            <NavLink
              to="/stats"
              className={({ isActive }) => (isActive ? "navbar-link selected" : "navbar-link")}
              onClick={resetNavbar}
            >
              {t("navbar.stats")}
            </NavLink>

            <NavLink
              to="/FAQ"
              className={({ isActive }) => (isActive ? "navbar-link selected" : "navbar-link")}
              onClick={resetNavbar}
            >
              {t("navbar.faq")}
            </NavLink>

            <a
              href="https://www.velofute.com/"
              className="navbar-link-redirection"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("navbar.velofute")}
            </a>

            {user && accountType === "admin" && (
              <NavLink
                to="/set-race-ranking"
                className={({ isActive }) => (isActive ? "navbar-link selected" : "navbar-link")}
                onClick={resetNavbar}
              >
                {t("navbar.admin")}
              </NavLink>
            )}

            {user && (
              <NavLink
                to="/logout"
                className={({ isActive }) => (isActive ? "navbar-link selected" : "navbar-link")}
                onClick={resetNavbar}
              >
                {t("navbar.logout")}
              </NavLink>
            )}
          </nav>

          <FlagSwitcher />
        </div>
      ) : (
        <div className="navbar-mobile">
          <div className="navbar-menu-icon" onClick={handleNavbarToggle} ref={menuMobileIconRef}>
            <svg
              className={`navbar-hamburger-icon ${navbarActive ? "active" : ""}`}
              width="100"
              height="100"
              viewBox="0 0 100 100"
            >
              <g filter="url(#a)">
                <rect width="57" height="9" x="44" y="42" rx="4.5" />
                <rect width="45" height="9" x="44" y="59" rx="4.5" />
                <rect width="51" height="9" x="44" y="76" rx="4.5" />
              </g>
              <defs>
                <filter
                  id="a"
                  width="395"
                  height="343"
                  x="-106"
                  y="-94"
                  colorInterpolationFilters="sRGB"
                  filterUnits="userSpaceOnUse"
                >
                  <feFlood floodOpacity="0" result="BackgroundImageFix" />
                  <feColorMatrix
                    in="SourceAlpha"
                    result="hardAlpha"
                    values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                  />
                  <feOffset dy="4" />
                  <feGaussianBlur stdDeviation="75" />
                  <feComposite in2="hardAlpha" operator="out" />
                  <feColorMatrix values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0" />
                  <feBlend in2="BackgroundImageFix" result="effect1_dropShadow_450_2451" />
                  <feBlend in="SourceGraphic" in2="effect1_dropShadow_450_2451" result="shape" />
                </filter>
              </defs>
            </svg>
          </div>

          <nav
            aria-label="mobile navigation"
            className={`navbar-sidebar ${navbarActive ? "active" : ""}`}
            ref={menuMobileRef}
          >
            <Link to="/" className="navbar-sidebar-link" onClick={resetNavbar}>
              {t("navbar.home")}
            </Link>
            <Link to="/profil" className="navbar-sidebar-link" onClick={resetNavbar}>
              {t("navbar.team")}
            </Link>
            <Link to="/calendar" className="navbar-sidebar-link" onClick={resetNavbar}>
              {t("navbar.calendar")}
            </Link>
            <Link to="/quoting" className="navbar-sidebar-link" onClick={resetNavbar}>
              {t("navbar.quoting")}
            </Link>
            <Link to="/scale" className="navbar-sidebar-link" onClick={resetNavbar}>
              {t("navbar.scale")}
            </Link>
            <Link to="/rules" className="navbar-sidebar-link" onClick={resetNavbar}>
              {t("navbar.rules")}
            </Link>
            <Link to="/stats" className="navbar-sidebar-link" onClick={resetNavbar}>
              {t("navbar.stats")}
            </Link>
            <Link to="/FAQ" className="navbar-sidebar-link" onClick={resetNavbar}>
              {t("navbar.faq")}
            </Link>

            <a
              href="https://www.velofute.com/"
              className="navbar-sidebar-link"
              target="_blank"
              rel="noopener noreferrer"
              onClick={resetNavbar}
            >
              {t("navbar.velofute")}
            </a>

            {user && accountType === "admin" && (
              <Link to="/set-race-ranking" className="navbar-sidebar-link" onClick={resetNavbar}>
                {t("navbar.admin")}
              </Link>
            )}

            {user && (
              <Link to="/logout" className="navbar-sidebar-link" onClick={resetNavbar}>
                {t("navbar.logout")}
              </Link>
            )}

            <div style={{ marginTop: 12 }}>
              <FlagSwitcher />
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Navbar;
